import React, { memo, useMemo } from 'react';
import { Accordion, Stack, Text } from 'rsuite';
import { convertNumberToCurrency, titleize } from 'src/common/utils';
import TaxPayableBreakdownTable from '../../BreakdownTable/TaxPayableBreakdownTable';
import { SummaryModal } from '../SummaryModal';
import {
  CalculatedLicense,
  CalculatedPeriodLicense,
  TaxableActivity,
} from 'src/types/tax';
import { useResize } from 'src/hooks/useResize';

interface ActivityDetailsProps {
  license: CalculatedPeriodLicense;
  onClose: () => void;
  panelKey: string;
  period: CalculatedLicense;
  show: boolean;
}

const ActivityDetailsComponent = ({
  license,
  onClose,
  panelKey,
  period,
  show,
}: ActivityDetailsProps) => {
  const { isMobile } = useResize();
  const total = useMemo(
    () => convertNumberToCurrency(license.totalDueForPeriod ?? 0),
    [license.totalDueForPeriod]
  );
  const title = useMemo(() => titleize(license.address), [license.address]);
  const modalOpen = useMemo(() => show && isMobile, [show, isMobile]);
  const accordionOpen = useMemo(() => show && !isMobile, [show, isMobile]);

  const totalPaymentDueRaw = useMemo(
    () =>
      license.providers.reduce(
        (total: number, provider: TaxableActivity) =>
          total + (provider.totalDueForPeriod ?? 0),
        0
      ),
    [license.providers]
  );
  return (
    <>
      <Accordion.Panel
        className="tax-accordion-header"
        eventKey={panelKey}
        header={
          <Stack justifyContent="space-between">
            <Stack.Item>
              <Text
                className="text-medium"
                style={{ fontWeight: 400 }}
              >
                {title}
              </Text>
            </Stack.Item>
            <Stack.Item>
              <Text
                className="text-medium pr-16"
                style={{ fontWeight: 400 }}
              >
                {total}
              </Text>
            </Stack.Item>
          </Stack>
        }
        expanded={accordionOpen}
      >
        {!isMobile && (
          <div className="tax-accordion-content">
            <TaxPayableBreakdownTable
              period={period}
              license={license.providers}
              periodLabel={period.label}
            />
          </div>
        )}
      </Accordion.Panel>

      {isMobile && (
        <SummaryModal
          isOpen={modalOpen}
          onClose={onClose}
          periodLabel={period.label ?? ''}
          address={license.address ?? ''}
        >
          <TaxPayableBreakdownTable
            period={period}
            license={license.providers}
            periodLabel={period?.label ?? ''}
            totalPayment={totalPaymentDueRaw}
          />
        </SummaryModal>
      )}
    </>
  );
};

export const ActivityDetails = memo(ActivityDetailsComponent);
ActivityDetails.displayName = 'ActivityDetails';
